
import { StatCard } from "@/components/StatCard";
import { Wallet, Lock, Coins, AlertTriangle, Ban, Trophy } from "lucide-react";
import { useNetworkStats } from "@/hooks/useNetworkStats";

interface NodeStatsGridProps {
  formattedStake: string;
  formattedReward: string;
  formattedLockedAmt: string;
  hardFaults: number;
  faults: number;
  rankDisplay: string;
  duskPrice: number;
  data: {
    stake: number;
    currentReward: number;
    lockedAmt: number;
  };
}

export function NodeStatsGrid({
  formattedStake,
  formattedReward,
  formattedLockedAmt,
  hardFaults,
  faults,
  rankDisplay,
  duskPrice,
  data,
}: NodeStatsGridProps) {
  // Use the network stats hook that powers the active nodes component
  const { data: networkStatsData } = useNetworkStats();
  
  // Get the active nodes count directly from network stats
  // This value comes from the network_stats view in the database
  const activeNodesCount = networkStatsData?.activeNodesCount || 0;
  
  const formattedRank = rankDisplay.startsWith('#') ? 
    `${rankDisplay}/${activeNodesCount}` : 
    `#-/${activeNodesCount}`;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <StatCard
        title="Amount Staked"
        value={`${formattedStake} DUSK`}
        tooltipContent="Total amount staked in the node"
        icon={<Wallet className="h-4 w-4" />}
        usdValue={data.stake * duskPrice}
        className="min-h-[120px]"
      />
      <StatCard
        title="Hard Slashes"
        value={String(hardFaults)}
        tooltipContent="Number of hard slashes received by this node"
        icon={<Ban className="h-4 w-4" />}
        className="min-h-[120px]"
      />
      <StatCard
        title="Available Rewards"
        value={`${formattedReward} DUSK`}
        tooltipContent="Rewards available to claim from this node"
        icon={<Coins className="h-4 w-4" />}
        usdValue={data.currentReward * duskPrice}
        className="min-h-[120px]"
      />
      <StatCard
        title="Soft Slashes"
        value={String(faults)}
        tooltipContent="Number of soft slashes received by this node"
        icon={<AlertTriangle className="h-4 w-4" />}
        className="min-h-[120px]"
      />
      <StatCard
        title="Amount Locked"
        value={`${formattedLockedAmt} DUSK`}
        tooltipContent="Amount currently locked in this node"
        icon={<Lock className="h-4 w-4" />}
        usdValue={data.lockedAmt * duskPrice}
        className="min-h-[120px]"
      />
      <StatCard
        title="Node Ranking"
        value={formattedRank}
        tooltipContent={
          <div className="max-w-[300px] p-4">
            <p>Node ranking is calculated based on performance efficiency:</p>
            <ul className="list-disc ml-4 mt-2 space-y-1">
              <li>Recent Performance: Total rewards earned in the last hour</li>
              <li>Efficiency Score: Hourly rewards divided by stake amount</li>
              <li>Ranking: Nodes are ranked by their efficiency score (higher is better)</li>
              <li>Updates: Rankings refresh every hour with new performance data</li>
            </ul>
            <p className="mt-2 text-sm text-muted-foreground">
              This system rewards nodes that consistently generate more rewards relative to their stake size.
            </p>
          </div>
        }
        icon={<Trophy className="h-4 w-4 text-dusk" />}
        showUpdateCountdown={true}
        lastUpdated={new Date()}
        className="min-h-[120px] bg-gradient-to-br from-background to-secondary/30"
      />
    </div>
  );
}
